// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';
import SubscriptionCard from '../../UIControls/SubscriptionCard';
import subscriptionPlans from './subscriptionPlans';
import { CenteredWrapper } from '../../UIControls/centered-wrapper';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import ChangePlanModal from '../../UIControls/ChangePlanModal';
import SettingsCancelPlan from './settings-cancel-plan';
import CancelPlanModal from '../../UIControls/CancelPlanModal';
import { useSnackbar } from '../SnackbarProvider';
import { toJS } from 'mobx';
import PaymentApi from '../../../rest-api/payment-api';
import UserApi from '../../../rest-api/user-api';
import { useStripe, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import config from '../../../config';
import getDiscountByPlan from '../../helper/getDiscountHelper';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import mixpanel from 'mixpanel-browser';
import { ReactComponent as CetraLogo } from '../../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../../assets/icons/LogoCircle.svg';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    borderRadius: '58px',
    border: '1px solid #e5e5e5',
    backgroundColor: '#ffffff',
    padding: '4px',
    gap: '4px',
    width: '205px',
    height: '50px',
    overflow: 'hidden',
    '& .MuiToggleButtonGroup-grouped': {
        border: 'none',
        borderRadius: '50px !important',
        margin: 0,
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
        borderLeft: 'none',
    },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme, selected }) => ({
    color: selected ? '#fff' : '#000453',
    backgroundColor: selected ? '#000453' : 'transparent',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '50px',
    minWidth: 0,
    padding: '8px 24px',
    '&:hover': {
        backgroundColor: selected ? '#000453' : 'rgba(0,4,83,0.06)',
    },
    '&.Mui-selected': {
        backgroundColor: '#000453',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#000453',
        },
    },
    border: 'none',
    flex: 1,
    '&.MuiToggleButtonGroup-grouped': {
        borderRadius: '50px !important',
    },
}));

const ManageSubscription = () => {
    const stripe = useStripe();
    const planKeys = Object.keys(subscriptionPlans);
    const { authStore, userDataStore } = useContext(StoreContext);
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const [subscriptionExpiryTime, setSubscriptionExpiryTime] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [, setPaymentDiscount] = useState(0); // that was not in use; Changed to 0% CETRA-371
    const [, setPaymentDetailsBillingCycle] = useState(50);

    const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
    const [cancelPlanModalOpen, setCancelPlanModalOpen] = useState(false);
    const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
    const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);
    const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
    const [reactivatePlan, setReactivatePlan] = useState(null);

    // track -  we are waiting on any payment/subscription API calls
    const [isProcessing, setIsProcessing] = useState(false);
    const showSnackbar = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const stripeCustomerId = userDataStore?.subscriptionData?.stripeCustomerId;
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showAnnualDiscountText = billingCycle === 'annually';
    const [discounts, setDiscounts] = useState({});
    const pendingPlan = userDataStore?.subscriptionData?.pendingPlan;
    const pendingPlanEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;
    const pendingPlanEndDateLabel = pendingPlanEndDate
        ? new Date(pendingPlanEndDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : null;
    const downgradeEffectiveDateLabel = userDataStore?.subscriptionData?.subscriptionEndDate
        ? new Date(userDataStore.subscriptionData.subscriptionEndDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : null;
    const currentAccountType = userDataStore?.subscriptionData?.accountType;
    const downgradeEffectiveDateText = downgradeEffectiveDateLabel || 'the end of your current billing period';
    const isClinicToProfessionalDowngrade = currentAccountType === 'Clinic' && selectedPlan === 'Professional';
    const isClinicToFreeDowngrade = currentAccountType === 'Clinic';
    const getSubscriptionEndTime = (subscriptionData = userDataStore?.subscriptionData) => {
        const subscriptionEndDate = subscriptionData?.subscriptionEndDate;

        if (!subscriptionEndDate) {
            return null;
        }

        const numericEndTime = Number(subscriptionEndDate);
        if (Number.isFinite(numericEndTime) && numericEndTime > 0) {
            return numericEndTime;
        }

        const parsedEndTime = new Date(subscriptionEndDate).getTime();
        return Number.isFinite(parsedEndTime) ? parsedEndTime : null;
    };
    const isSubscriptionExpiredCancelled = (subscriptionData = userDataStore?.subscriptionData) => {
        if (!subscriptionData?.cancelled) {
            return false;
        }

        const subscriptionEndTime = getSubscriptionEndTime(subscriptionData);
        return !subscriptionEndTime || Date.now() > subscriptionEndTime;
    };
    const isExpiredCancelled = isSubscriptionExpiredCancelled();
    const planIntroTextSx = {
        fontSize: { xs: '12px', md: '18px' },
        fontWeight: 400,
        fontFamily: 'Inter',
        fontStyle: 'normal',
        color: '#616063',
        letterSpacing: { xs: '-0.24px', md: '-0.36px' },
        lineHeight: { xs: '22px', md: '28px' },
        maxWidth: { xs: 290, md: 749 },
        margin: '0 auto',
        textAlign: 'center',
        mb: 0,
    };

    useEffect(() => {
        if (isMultiUser) {
            navigate('/settings-multiuser', { replace: true });
        }
    }, [isMultiUser, navigate]);

    const fetchPaymentMethod = async () => {
        try {
            if (stripeCustomerId) {
                const paymentDetails = await PaymentApi.getPaymentDetails(authStore.idToken, stripeCustomerId);
                console.warn(paymentDetails);
                setPaymentDetails(paymentDetails);
                if (paymentDetails?.subscription?.discount?.coupon?.percent_off) {
                    console.warn('coupon found ', paymentDetails.subscription.discount.coupon);
                    setPaymentDiscount(paymentDetails.subscription.discount.coupon.percent_off);
                }
                if (paymentDetails?.subscription?.plan) {
                    setPaymentDetailsBillingCycle(paymentDetails?.subscription?.plan.interval);
                }
            }
        } catch (error) {
            console.error('Error fetching payment method:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchDiscounts = async () => {
            const newDiscounts = {};

            for (const plan of planKeys) {
                const discount = await getDiscountByPlan(authStore.idToken, plan);
                // getDiscountByPlan returns a percentage number, so wrap it in our new format
                newDiscounts[plan] = discount ? { type: 'percent', value: discount } : null;
            }

            console.warn('Fetched Discounts: ', newDiscounts);
            setDiscounts(newDiscounts);
        };

        fetchDiscounts();
    }, [authStore.idToken]);

    useEffect(() => {
        fetchPaymentMethod();
    }, [userDataStore, authStore]);

    useEffect(() => {
        window.scrollTo(0, 0);
        console.warn(toJS(userDataStore));
        if (location.state && location.state.subscriptionUpdated) {
            showSnackbar(
                'Your subscription details have been successfully updated. Start enjoying the benefits now!',
                'success',
            );
            // clear state to prevent the message from showing again on subsequent navigations
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, showSnackbar, navigate]);

    useEffect(() => {
        const subscriptionEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;
        if (subscriptionEndDate) {
            const currentTime = Date.now();
            const endTimeInMilliseconds = new Date(subscriptionEndDate).getTime();
            const differenceInMilliseconds = endTimeInMilliseconds - currentTime;
            const millisecondsInOneDay = 24 * 60 * 60 * 1000;
            const differenceInDays = differenceInMilliseconds / millisecondsInOneDay;
            setSubscriptionExpiryTime(Math.max(0, Math.floor(differenceInDays)));
        } else {
            setSubscriptionExpiryTime(null);
        }
    }, [userDataStore?.subscriptionData?.subscriptionEndDate]);

    const getPendingUpgradeHandoutId = () => {
        const getPendingId = (key) => {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            try {
                const data = JSON.parse(raw);
                const isRecent = Date.now() - data.timestamp < 3600000;
                if (!isRecent || !data.handoutId) {
                    localStorage.removeItem(key);
                    return null;
                }
                return data.handoutId;
            } catch (error) {
                localStorage.removeItem(key);
                return null;
            }
        };

        const pendingFromStorage =
            getPendingId('pendingCombinedHandoutSave') ||
            getPendingId('pendingCustomisedHandoutSave') ||
            getPendingId('pendingWhiteboardSave');

        if (pendingFromStorage) {
            return pendingFromStorage;
        }

        const pendingUpgradeHandoutId = sessionStorage.getItem('pendingUpgradeHandoutId');
        const pendingUpgradeSetAt = Number(sessionStorage.getItem('pendingUpgradeHandoutIdSetAt') || 0);
        const isRecent =
            pendingUpgradeHandoutId && pendingUpgradeSetAt > 0 && Date.now() - pendingUpgradeSetAt < 300000;

        if (!isRecent && pendingUpgradeHandoutId) {
            sessionStorage.removeItem('pendingUpgradeHandoutId');
            sessionStorage.removeItem('pendingUpgradeHandoutIdSetAt');
            return null;
        }

        return pendingUpgradeHandoutId;
    };

    const handlePostSubscriptionRedirect = (previousPlan, newPlan, userDataStore, navigate, showSnackbar, response) => {
        let shouldRedirectToSettings = false;

        if (previousPlan === 'Student' && (newPlan === 'Professional' || newPlan === 'Clinic')) {
            shouldRedirectToSettings = true;
        } else if (previousPlan === 'Professional' && newPlan === 'Clinic') {
            const clinicName = userDataStore.userData.clinicName;
            if (!clinicName || clinicName.trim() === '') {
                shouldRedirectToSettings = true;
            }
        }

        if (shouldRedirectToSettings) {
            navigate('/settings', { state: { scrollToPracticeDetails: true } });
        } else if (response.subscription) {
            const pendingHandoutId = getPendingUpgradeHandoutId();
            if (pendingHandoutId) {
                showSnackbar(
                    'Your subscription details have been successfully updated. Open your saved handout to continue.',
                    'success',
                );
                return;
            }
            navigate('/', { state: { subscriptionSuccess: true } });
        } else {
            showSnackbar('Billing details have been successfully updated.', 'success');
        }
    };

    const trackFreeUpgrade = (previousPlan, newPlan, userId, email) => {
        if (previousPlan === 'Free' && newPlan === 'Professional') {
            mixpanel.track('UpgradeFromFreeToProfessional', { userId, email });
        } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
            mixpanel.track('UpgradeFromFreeToClinic', { userId, email });
        }
    };

    const trackDowngradeToFree = (previousPlan, newPlan, userId, email) => {
        if (newPlan !== 'Free') return;
        if (previousPlan === 'Professional') {
            mixpanel.track('DowngradeFromProfessionalToFree', { userId, email });
        } else if (previousPlan === 'Clinic') {
            mixpanel.track('DowngradeFromClinicToFree', { userId, email });
        }
    };

    const handleBillingCycleChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const handleConfirmReactivate = async () => {
        setReactivateConfirmOpen(false);
        if (reactivatePlan === 'Free') {
            await executeFreeDowngrade();
        } else {
            // For paid plans, navigate to checkout
            const checkoutParams = new URLSearchParams({
                plan: reactivatePlan,
                billingCycle,
                resubscribe: 'true',
            });
            navigate(`/checkout?${checkoutParams.toString()}`, {
                state: { plan: reactivatePlan, billingCycle, resubscribe: true },
            });
        }
    };

    const executeFreeDowngrade = async () => {
        try {
            setIsProcessing(true);
            console.log('Free plan selected', {
                userId: userDataStore.userId,
                stripeCustomerId,
                hasSubscription: !!paymentDetails?.subscription,
                currentPlan: userDataStore.subscriptionData?.accountType,
            });

            const freePlan = subscriptionPlans.Free;
            const freePlanDetails = {
                priceId: freePlan.priceMonthlyId,
                productId: freePlan.productId,
                isMonthly: true,
                accountType: freePlan.accountType,
            };

            // If already Free and not cancelled, no further action needed
            if (userDataStore.subscriptionData?.accountType === 'Free' && !userDataStore.subscriptionData?.cancelled) {
                showSnackbar('You are already on the Free plan', 'success');
                return;
            }

            if (userDataStore.subscriptionData?.cancelled) {
                if (!isSubscriptionExpiredCancelled()) {
                    showSnackbar('Your current plan remains active until the end of the billing period.', 'info');
                    return;
                }

                const paymentResponse = await PaymentApi.updatePaymentDetails(authStore.idToken, {
                    userId: userDataStore.userId,
                    email: userDataStore.userData.emailAddress,
                    plan: freePlanDetails,
                    resubscribe: true,
                });

                if (paymentResponse?.error) {
                    throw new Error(paymentResponse.error);
                }

                const subscriptionData = {
                    ...userDataStore.subscriptionData,
                    accountType: 'Free',
                    trialActive: false,
                    trialEndDate: null,
                    subscriptionEndDate: paymentResponse?.subscription?.current_period_end
                        ? paymentResponse.subscription.current_period_end * 1000
                        : null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: paymentResponse?.customerId || stripeCustomerId || null,
                };

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData,
                });

                await authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, userDataStore.userData.emailAddress, 'Free');

                showSnackbar('Free plan activated', 'success');
                navigate('/', { state: { subscriptionSuccess: true } });
                return;
            }

            if (!stripeCustomerId || !paymentDetails?.subscription) {
                // No active subscription found - provision Free subscription in Stripe
                const paymentResponse = await PaymentApi.updatePaymentDetails(authStore.idToken, {
                    userId: userDataStore.userId,
                    email: userDataStore.userData.emailAddress,
                    plan: freePlanDetails,
                });

                const subscriptionData = {
                    accountType: 'Free',
                    trialActive: false,
                    trialEndDate: null,
                    subscriptionEndDate: paymentResponse?.subscription?.current_period_end
                        ? paymentResponse.subscription.current_period_end * 1000
                        : null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: paymentResponse?.customerId || stripeCustomerId || null,
                };

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData,
                });

                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, userDataStore.userData.emailAddress, 'Free');

                showSnackbar('Free plan activated', 'success');
                navigate('/', { state: { subscriptionSuccess: true } });
                return;
            }

            // Cancel existing subscription at period end and schedule downgrade
            const cancelResponse = await PaymentApi.cancelSubscription(authStore.idToken, {
                userId: userDataStore.userId,
            });
            console.log('Cancel subscription response', cancelResponse);

            const currentPeriodEnd = cancelResponse?.subscription?.current_period_end
                ? cancelResponse.subscription.current_period_end * 1000
                : userDataStore?.subscriptionData?.subscriptionEndDate || null;

            const subscriptionData = {
                ...userDataStore.subscriptionData,
                cancelled: true,
                subscriptionEndDate: currentPeriodEnd,
                pendingPlan: {
                    accountType: 'Free',
                    productId: freePlanDetails.productId,
                    priceId: freePlanDetails.priceId,
                    isMonthly: freePlanDetails.isMonthly,
                },
            };

            await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

            userDataStore.setUserData({
                ...userDataStore.getUserData(),
                subscriptionData,
            });

            authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

            trackDowngradeToFree(
                userDataStore.subscriptionData?.accountType,
                'Free',
                userDataStore.userId,
                userDataStore.userData.emailAddress,
            );

            const resp = await KlaviyoApi.updateUserInKlaviyo(
                authStore.idToken,
                userDataStore.userData.emailAddress,
                userDataStore.subscriptionData?.accountType,
            );
            console.log('Klaviyo downgrade response:', resp);

            showSnackbar('Downgrade scheduled for the end of your billing period', 'success');
            window.location.reload();
        } catch (err) {
            console.error('Error downgrading to Free plan:', err);
            showSnackbar('Error when downgrading to Free plan', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCardClick = async (plan) => {
        if (isMultiUser) return;
        if (!authStore?.idToken) {
            await authStore.refreshToken();
        }
        if (!authStore?.idToken) {
            showSnackbar('Authentication is still loading. Please try again in a moment.', 'error');
            return;
        }

        const subscriptionData = userDataStore?.subscriptionData;
        const subscriptionEndTime = getSubscriptionEndTime(subscriptionData);
        const currentTime = Date.now();
        const expiredCancelled = isSubscriptionExpiredCancelled(subscriptionData);
        console.warn(userDataStore.subscriptionData?.accountType, plan, subscriptionEndTime, currentTime);

        // If user is already on that plan and the plan is still active, do nothing.
        if (
            userDataStore.subscriptionData?.accountType === plan &&
            !expiredCancelled &&
            (plan === 'Free' || (subscriptionEndTime && currentTime < subscriptionEndTime))
        ) {
            return false;
        }

        // If plan is Enterprise, open a *new* Enterprise-only modal (no Stripe flows)
        if (plan === 'Enterprise') {
            setEnterpriseModalOpen(true);
            return; // Stop any further plan-change logic
        }

        if (subscriptionData?.cancelled && !expiredCancelled) {
            showSnackbar('Your current plan remains active until the end of the billing period.', 'info');
            return;
        }

        // If plan is Free, downgrade immediately (no payment needed)
        if (plan === 'Free') {
            // If already Free and not cancelled, no action needed
            if (userDataStore.subscriptionData?.accountType === 'Free' && !userDataStore.subscriptionData?.cancelled) {
                showSnackbar('You are already on the Free plan', 'success');
                return;
            }
            // If cancelled and expired, show resubscribe confirmation
            if (userDataStore.subscriptionData?.cancelled) {
                setReactivatePlan('Free');
                setReactivateConfirmOpen(true);
                return;
            }
            setDowngradeConfirmOpen(true);
            return;
        }

        // If user is cancelled and selecting a paid plan, show reactivation confirmation
        if (userDataStore.subscriptionData?.cancelled) {
            setReactivatePlan(plan);
            setReactivateConfirmOpen(true);
            return;
        }

        console.warn('paymentDetails should have both keys: ', paymentDetails);

        const hasActiveSubscription = paymentDetails?.subscription;
        const hasPaymentMethods = paymentDetails?.paymentMethods && paymentDetails.paymentMethods.length > 0;
        console.warn(
            hasActiveSubscription,
            hasPaymentMethods,
            stripeCustomerId,
            userDataStore.subscriptionData,
            subscriptionEndTime,
            currentTime,
        );
        if (
            !stripeCustomerId || // customer is yet to be added to stripe
            !userDataStore.subscriptionData || // No subscription data means no paid plan
            !subscriptionEndTime || // Subscription end date is undefined (not set)
            currentTime >= subscriptionEndTime || // Subscription has expired
            !hasActiveSubscription || // No active subscription
            !hasPaymentMethods // No payment methods saved
        ) {
            navigate('/checkout', { state: { plan, billingCycle } });
        } else {
            setSelectedPlan(plan);
            setChangePlanModalOpen(true);
        }
    };

    const handleConfirmChangePlan = async () => {
        setChangePlanModalOpen(false);
        // Start spinner
        setIsProcessing(true);
        try {
            console.warn('current plan', userDataStore.subscriptionData?.accountType);

            console.warn('new plan', selectedPlan);

            const planDetails = subscriptionPlans[selectedPlan];

            planDetails.isMonthly = billingCycle === 'monthly';
            planDetails.priceId = billingCycle === 'monthly' ? planDetails.priceMonthlyId : planDetails.priceAnnuallyId;

            console.warn('new plan details ', planDetails);

            console.warn('selected billing cycle ', billingCycle);

            console.warn('use payment method: ', paymentDetails['paymentMethods'][0]);

            await sendUpdateSubscriptionToBackend(paymentDetails['paymentMethods'][0], planDetails);
        } catch (error) {
            console.error(error);
        } finally {
            // Stop spinner
            setIsProcessing(false);
        }
    };

    const sendUpdateSubscriptionToBackend = async (paymentMethod, plan) => {
        try {
            const req = {
                userId: userDataStore.userId,
                paymentMethodId: paymentMethod.id,
                email: userDataStore.userData.emailAddress,
                ...(plan && { plan }),
            };

            const bearerToken = authStore.idToken;

            // payment method is not needed in req but have for future just in case
            const response = await PaymentApi.updateSubscription(bearerToken, req);

            console.warn('update subscription  response: ', response);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            if (response.requiresAction) {
                showSnackbar('Payment pending - please complete the security check with your bank', 'success');
                const result = await stripe.confirmCardPayment(response.paymentIntentClientSecret);
                if (result.error) {
                    showSnackbar('3D Secure authentication failed.', 'error');
                } else {
                    // Once 3D-Secure is done
                    if (
                        result.paymentIntent.status === 'succeeded' ||
                        result.paymentIntent.status === 'requires_confirmation'
                    ) {
                        await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                    }
                }
            } else if (response.subscription) {
                // Handle subscription success
                const { current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: plan?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('sub data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData.accountType;
                const newPlan = plan?.accountType;

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    plan?.accountType,
                );
                trackFreeUpgrade(previousPlan, newPlan, userDataStore.userId, userDataStore.userData.emailAddress);

                handlePostSubscriptionRedirect(previousPlan, newPlan, userDataStore, navigate, showSnackbar, response);
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when updating billing details, please try again.', 'error');
        }
    };

    const confirmPaymentIntent = async (paymentIntentId, subscriptionId) => {
        console.warn('Check this Payment Intent: ', paymentIntentId);
        try {
            const req = {
                paymentIntentId,
                userId: userDataStore.userId,
                subscriptionId: subscriptionId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.confirmPaymentDetails(bearerToken, req);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            const planDetails = subscriptionPlans[selectedPlan];

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.subscription) {
                const { current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: planDetails?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('subscription data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData.accountType; // store BEFORE updating
                const newPlan = planDetails?.accountType;

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, userDataStore.userData.emailAddress, newPlan);
                trackFreeUpgrade(previousPlan, newPlan, userDataStore.userId, userDataStore.userData.emailAddress);

                // Re-fetch payment methods to update state
                await fetchPaymentMethod();

                handlePostSubscriptionRedirect(previousPlan, newPlan, userDataStore, navigate, showSnackbar, response);
            }
        } catch (error) {
            console.error('Error confirming payment intent:', error);
            showSnackbar('Error when confirming payment.', 'error');
        }
    };

    const handleConfirmCancelPlan = async (checkedReasons) => {
        try {
            await sendCancelSubscriptionToBackend(checkedReasons);
            setCancelPlanModalOpen(false);
            window.location.reload();
        } catch (error) {
            // Not reloading the page on error
            console.error('Cancellation failed, page will not reload:', error);
        }
    };

    const sendCancelSubscriptionToBackend = async (checkedReasons) => {
        try {
            const req = {
                userId: userDataStore.userId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.cancelSubscription(bearerToken, req);

            if (response.error) {
                console.error('Error canceling payment:', response.error);
                // maybe sync issue between stripe and our db debug here?
                throw new Error(response.error || 'Failed to cancel subscription');
            }

            if (response.subscription) {
                const { current_period_end } = response.subscription;

                const existingSubscriptionData = userDataStore.subscriptionData;

                // they can still use subscription until end of billing peroid, only cancelled = true
                const subscriptionData = {
                    ...existingSubscriptionData,
                    cancelled: true,
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancellationReason: checkedReasons,
                };

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                // Update userDataStore with the new subscription details
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData,
                });

                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    'Cancelled',
                );
                mixpanel.track('SubscriptionCancelled', {
                    userId: userDataStore.userId,
                    email: userDataStore.userData.emailAddress,
                });
                showSnackbar('Your subscription plan has been successfully cancelled.');
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when cancelling subscription.', 'error');
            // if no throw error here, then page will reload, hance the err not visible/readable
            throw error;
        }
    };

    const isSubscriptionDataReady = !!userDataStore?.subscriptionData?.accountType;
    const shouldShowLoading = loading || !isSubscriptionDataReady;
    const isPlanCurrentlyActive = (plan) => {
        const subscriptionData = userDataStore?.subscriptionData;
        const subscriptionEndTime = getSubscriptionEndTime(subscriptionData);

        if (subscriptionData?.accountType !== plan || isSubscriptionExpiredCancelled(subscriptionData)) {
            return false;
        }

        return plan === 'Free' || (subscriptionEndTime && Date.now() < subscriptionEndTime);
    };

    return (
        <CenteredWrapper
            component="main"
            sx={{
                zIndex: 1,
                position: 'relative',
                backgroundColor: '#F5F6FB',
                p: 0,
                px: { xs: 2, md: 3 },
                py: { xs: 4, md: 6 },
                pb: { xs: 4, md: 8 },
            }}
        >
            {shouldShowLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Cetra logo — same as onboarding plan screens */}
                    <Box sx={{ mb: { xs: 2, md: 3 } }}>
                        <Box sx={{ position: 'relative', width: { xs: 50, md: 64 }, height: { xs: 50, md: 64 } }}>
                            <Box
                                component={LogoCircle}
                                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                            />
                            <Box
                                component={CetraLogo}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: { xs: 29, md: 42 },
                                    height: { xs: 22, md: 32 },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        className="settings-container"
                        sx={{
                            width: '100%',
                            maxWidth: 1245,
                            minWidth: { md: 1245 },
                            backgroundColor: '#fff',
                            borderRadius: '30px',
                            p: { xs: 3, md: '40px 46px' },
                            border: '1px solid rgba(102, 102, 102, 0.15)',
                        }}
                    >
                        <Box>
                            <Button
                                onClick={() => navigate(isMultiUser ? '/settings-multiuser' : '/settings')}
                                sx={{
                                    minWidth: 0,
                                    width: { xs: '23px', md: 40 },
                                    height: { xs: '23px', md: 40 },
                                    borderRadius: { xs: '11.5px', md: '50%' },
                                    ml: 'auto',
                                    display: 'flex',
                                    color: '#4D4C4D',
                                    backgroundColor: { xs: '#F7F7F7', md: '#ECECEC' },
                                    '&:hover': { backgroundColor: { xs: '#F7F7F7', md: '#DFDFDF' } },
                                }}
                                aria-label="Close"
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: { xs: '16.816px', md: '16px' },
                                        height: { xs: '16.816px', md: '16px' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            width: { xs: '16.816px', md: '16px' },
                                            height: { xs: '1.401px', md: '1.8px' },
                                            borderRadius: { xs: '15.826px', md: '2px' },
                                            backgroundColor: '#4D4C4D',
                                            transform: 'translate(-50%, -50%) rotate(45deg)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            width: { xs: '16.816px', md: '16px' },
                                            height: { xs: '1.401px', md: '1.8px' },
                                            borderRadius: { xs: '15.826px', md: '2px' },
                                            backgroundColor: '#4D4C4D',
                                            transform: 'translate(-50%, -50%) rotate(-45deg)',
                                        }}
                                    />
                                </Box>
                            </Button>

                            <Typography
                                sx={{
                                    fontSize: { xs: '26px', md: '36px' },
                                    fontWeight: 600,
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    color: '#000453',
                                    letterSpacing: { xs: '-0.52px', md: '-0.72px' },
                                    lineHeight: { xs: '26px', md: '36px' },
                                    mb: '19px',
                                    textAlign: 'center',
                                }}
                            >
                                Manage your plan
                            </Typography>
                            <Typography sx={planIntroTextSx}>
                                Choose the plan that fits your needs. All plans include essential features to get you
                                started, with the flexibility to scale as you grow. No hidden fees and the freedom to
                                change anytime.
                            </Typography>
                            {pendingPlan?.accountType === 'Free' && pendingPlanEndDateLabel && (
                                <Typography sx={{ ...planIntroTextSx, mt: '4px' }}>
                                    {Date.now() > pendingPlanEndDate
                                        ? `Your previous plan ended on ${pendingPlanEndDateLabel}. Please select a plan to continue.`
                                        : `Downgrade scheduled. You will keep your current plan until ${pendingPlanEndDateLabel}.`}
                                </Typography>
                            )}
                            {/* CETRA-754: Keep the remaining-days notice as its own centered line above billing toggle. */}
                            {subscriptionExpiryTime <= 30 &&
                                Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate &&
                                userDataStore?.subscriptionData?.accountType !== 'Free' && (
                                    <Typography sx={{ ...planIntroTextSx, mt: '18px' }}>
                                        You have <strong> {subscriptionExpiryTime} days</strong> left of your{' '}
                                        {userDataStore?.subscriptionData?.accountType}{' '}
                                        {userDataStore?.subscriptionData?.trialActive ? 'free trial' : ''} plan.
                                    </Typography>
                                )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '32px' }}
                                >
                                    <StyledToggleButtonGroup
                                        value={billingCycle}
                                        exclusive
                                        onChange={handleBillingCycleChange}
                                        aria-label="billing cycle"
                                        sx={{ marginTop: isMobile ? 2 : 0 }}
                                    >
                                        <StyledToggleButton
                                            value="monthly"
                                            aria-label="monthly"
                                            selected={billingCycle === 'monthly'}
                                        >
                                            Monthly
                                        </StyledToggleButton>
                                        <StyledToggleButton
                                            value="annually"
                                            aria-label="annually"
                                            selected={billingCycle === 'annually'}
                                        >
                                            Annual
                                        </StyledToggleButton>
                                    </StyledToggleButtonGroup>

                                    {showAnnualDiscountText && (
                                        <Typography
                                            variant="body1"
                                            sx={{ fontSize: '0.875rem', textAlign: 'right', marginTop: 1 }} // Ensures it is always below the buttons
                                        >
                                            {config.annualDiscountText}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {isMultiUser && (
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        padding: 2,
                                        mb: 2,
                                        mt: 3,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#555' }}>
                                        ⚠️ You’re currently logged in as a clinic team member. Only the Administrator
                                        can manage or change the subscription plan.
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    mt: 3,
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                    gap: { xs: '16px', sm: '24px', lg: '46px' },
                                }}
                            >
                                {planKeys
                                    .filter((plan) => !subscriptionPlans[plan].legacy)
                                    .map((plan, index) => (
                                        <Box
                                            key={index}
                                            data-testid={`manage-plan-${plan.toLowerCase()}`}
                                            sx={{ cursor: 'pointer', height: '100%' }}
                                        >
                                            <SubscriptionCard
                                                plan={plan}
                                                discount={discounts[plan]}
                                                billingCycle={billingCycle}
                                                isCurrentPlan={isPlanCurrentlyActive(plan)}
                                                disabled={isMultiUser}
                                                onSelectPlan={handleCardClick}
                                            />
                                        </Box>
                                    ))}
                            </Box>

                            {userDataStore?.subscriptionData?.accountType &&
                                userDataStore?.subscriptionData?.accountType !== 'Free' &&
                                Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate && (
                                    <Typography sx={{ textAlign: 'center', fontWeight: 600, mt: 2, color: '#616063' }}>
                                        *exclusive discounts applied during sign up will not display here
                                    </Typography>
                                )}

                            <Box sx={{ textAlign: 'center', mt: '18px' }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '13px', md: '18px' },
                                        fontWeight: 400,
                                        color: '#616063',
                                        lineHeight: { xs: '20px', md: '28px' },
                                    }}
                                >
                                    Need flexible users, advanced permissions or bespoke licensing? Talk to us about our{' '}
                                    <Box
                                        component="span"
                                        onClick={() => setEnterpriseModalOpen(true)}
                                        sx={{ color: '#FF669B', textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                        Enterprise plan
                                    </Box>
                                </Typography>
                            </Box>

                            {!userDataStore?.subscriptionData?.trialActive && !isExpiredCancelled && (
                                <>
                                    <Divider sx={{ marginBottom: 4, mt: 4 }} />
                                    <SettingsCancelPlan
                                        subscriptionExpiryTime={userDataStore?.subscriptionData?.subscriptionEndDate}
                                        cancelled={userDataStore?.subscriptionData?.cancelled}
                                        onOpenCancelPlanModal={() => {
                                            setCancelPlanModalOpen(true);
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            <BlockingSpinner open={isProcessing} />

            {isClinicToProfessionalDowngrade ? (
                <ConfirmationDialog
                    open={changePlanModalOpen}
                    handleClose={() => {
                        setChangePlanModalOpen(false);
                    }}
                    onConfirm={handleConfirmChangePlan}
                    title="Thinking about changing your plan?"
                    message={
                        <>
                            You're currently on the Clinic plan, and your team will continue to have full access until{' '}
                            <strong style={{ whiteSpace: 'nowrap' }}>{downgradeEffectiveDateText}</strong>.
                            <br />
                            <br />
                            If you decide to downgrade, your team will lose access to Cetra and only the administrator
                            will retain access with Professional plan features.
                            <br />
                            <br />
                            If you'd prefer to stay on the Clinic plan, you can keep your team access and current
                            features with no interruption.
                        </>
                    }
                    confirmText="Confirm downgrade"
                    cancelText="Keep my Clinic plan"
                />
            ) : (
                <ChangePlanModal
                    open={changePlanModalOpen}
                    onClose={() => {
                        setChangePlanModalOpen(false);
                    }}
                    onConfirm={handleConfirmChangePlan}
                    selectedPlan={selectedPlan}
                    title="Great choice! You're just one click away from more features!"
                    message="Please confirm that you want to proceed with your upgrade."
                />
            )}

            <ConfirmationDialog
                open={downgradeConfirmOpen}
                handleClose={() => setDowngradeConfirmOpen(false)}
                onConfirm={executeFreeDowngrade}
                title="Thinking about changing your plan?"
                message={
                    isClinicToFreeDowngrade ? (
                        <>
                            You're currently on the Clinic plan, and your team will continue to have full access until{' '}
                            <strong style={{ whiteSpace: 'nowrap' }}>{downgradeEffectiveDateText}</strong>.
                            <br />
                            <br />
                            If you decide to downgrade, your team will lose access to Cetra and only the administrator
                            will retain access with Free plan features.
                            <br />
                            <br />
                            If you'd prefer to stay on the Clinic plan, you can keep your team access and current
                            features with no interruption.
                        </>
                    ) : downgradeEffectiveDateLabel ? (
                        <>
                            You're currently on the {userDataStore?.subscriptionData?.accountType} plan, and you'll
                            continue to enjoy full access until{' '}
                            <strong style={{ whiteSpace: 'nowrap' }}>{downgradeEffectiveDateLabel}</strong>.
                            <br />
                            <br />
                            If you decide to downgrade, nothing changes right away. Your plan will remain active until
                            the date above, and you won't be charged once the Free plan starts.
                            <br />
                            <br />
                            If you'd prefer to stay on {userDataStore?.subscriptionData?.accountType}, you can keep all
                            your features with no interruption.
                        </>
                    ) : (
                        <>
                            You're currently on the {userDataStore?.subscriptionData?.accountType} plan.
                            <br />
                            <br />
                            If you decide to downgrade, you won't be charged once the Free plan starts.
                            <br />
                            <br />
                            If you'd prefer to stay on {userDataStore?.subscriptionData?.accountType}, you can keep all
                            your features with no interruption.
                        </>
                    )
                }
                confirmText="Confirm downgrade"
                cancelText={`Keep my ${userDataStore?.subscriptionData?.accountType} plan`}
            />

            <ConfirmationDialog
                open={enterpriseModalOpen}
                handleClose={() => setEnterpriseModalOpen(false)}
                onConfirm={() => setEnterpriseModalOpen(false)} // single-button close
                title="Excellent choice!"
                message={
                    <>
                        Let's explore this further. Please email us at{' '}
                        <a
                            href="mailto:hello@cetrahealth.com"
                            style={{ fontWeight: 'bold', color: '#ff4081', textDecoration: 'none' }}
                        >
                            hello@cetrahealth.com
                        </a>{' '}
                        detailing the type of enterprise package you're interested in. If preferred, we can schedule a
                        meeting to discuss the finer details.
                    </>
                }
                confirmText="Close"
                cancelTextReq={false}
            />

            <ConfirmationDialog
                open={reactivateConfirmOpen}
                handleClose={() => setReactivateConfirmOpen(false)}
                onConfirm={handleConfirmReactivate}
                title="Ready to resubscribe?"
                message={
                    reactivatePlan === 'Free' ? (
                        <>
                            You're about to resubscribe to the <strong>Free</strong> plan.
                            <br />
                            <br />
                            This will give you access to all Free plan features immediately.
                        </>
                    ) : (
                        <>
                            You're about to resubscribe to the <strong>{reactivatePlan}</strong> plan.
                            <br />
                            <br />
                            You'll be taken to the checkout page to complete your subscription.
                        </>
                    )
                }
                confirmText={reactivatePlan === 'Free' ? 'Confirm resubscription' : 'Continue to checkout'}
                cancelText="Cancel"
            />

            <CancelPlanModal
                open={cancelPlanModalOpen}
                onClose={() => {
                    setCancelPlanModalOpen(false);
                }}
                onConfirm={handleConfirmCancelPlan}
            />
        </CenteredWrapper>
    );
};

const ManageSubscriptionWrapper = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <ManageSubscription {...props} />
        </Elements>
    );
};

export default ManageSubscriptionWrapper;
